// Relocated out of ComparableFilterRow.jsx. This is a move, not a redraw: every class, colour and
// icon below is carried over unchanged, with one exception — the wrapper div's className dropped
// `pb-[2px]` (previously `flex pb-[2px] gap-x-[5px] items-center`), which bottom-aligned the chips
// inside the old row's `items-end` container; this toggle now renders inside EstimateMetrics's own
// absolutely positioned wrapper, floating above that column's cards rather than flowing inside it,
// where that padding has no equivalent purpose.
//
// State was never owned by ComparableFilterRow.jsx to begin with — both buttons only read
// `filters.archivedView` and called `onFiltersChange`/`onFiltersBlur`, and `filters` itself already
// lives in pages/Arv.jsx. So moving the JSX here carries the state question with it for free:
// pages/Arv.jsx now passes the same `filters`/`onFiltersChange`/`onFiltersBlur` to this component
// instead of to ComparableFilterRow, and nothing new had to be lifted.

// The two icons are drawn rather than reused. Both existing assets had a hardcoded stroke, which is
// why the colour could not follow button state: `assets/arv/check.svg` was a 9x7 redraw at stroke
// width 2, and the Excluded button was still carrying `assets/pipeline/delete.svg` — a trashcan,
// where the requirement is an X icon.
//
// Geometry from the 18.08 export, translated out of the frames by subtracting each clip origin.
// The check is `check_15` inside a 13x10 clip at (8371, 1766); the cross is `Vector_252`, an 8x8
// diagonal pair at (8443.5, 1767) inside a button whose box starts at (8424.5, 1753.5). Both are
// stroke width 2.5 with a round cap, and both take currentColor so the button decides.
const IncludedIcon = () => (
  <svg width="13" height="10" viewBox="0 0 13 10" fill="none" aria-hidden="true">
    <path d="M2 5.21L4.95 8.54C4.97 8.56 4.99 8.58 5.01 8.59C5.03 8.6 5.06 8.6 5.08 8.6C5.1 8.6 5.13 8.6 5.15 8.59C5.17 8.58 5.19 8.56 5.21 8.54L11 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
)

const ExcludedIcon = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
    <path d="M8 0L0 8M8 8L0 0" stroke="currentColor" strokeWidth="2.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// The two accents are not the same colour, and that is the export's own answer rather than a choice:
// Frame 458_3 strokes Included #4CBC18 and Frame 458_6 strokes Excluded #C13A4A.
//
// Written out in full, per button and per state, and not composed from an accent variable. Tailwind
// finds classes by scanning the source for whole names, so a border utility whose colour is
// interpolated into a template literal generates no rule at all — the class name lands in the DOM
// and nothing stands behind it, which on this screen would have shipped a button whose border
// silently never changed colour. There is no build error and no test failure in that outcome, which
// is why IncludedExcludedToggle.test.jsx greps this source for the interpolation rather than reading
// the DOM.
const INCLUDED_STATE = {
  on:  'border-[#4CBC18] text-[#4CBC18]',
  off: 'border-[#C6CFC6] text-[#4F5F4A] hover:border-[#4CBC18] hover:text-[#4CBC18] focus-visible:border-[#4CBC18] focus-visible:text-[#4CBC18]',
}
const EXCLUDED_STATE = {
  on:  'border-[#C13A4A] text-[#C13A4A]',
  off: 'border-[#C6CFC6] text-[#4F5F4A] hover:border-[#C13A4A] hover:text-[#C13A4A] focus-visible:border-[#C13A4A] focus-visible:text-[#C13A4A]',
}

// The export draws the pair twice, once per selection, and reading both rows together is what
// gives the rule: `hover/focused-states/Frame 521_2` has Included on
// #4CBC18 with Excluded on #C6CFC6, and `Frame 522` has Excluded on #C13A4A with Included on
// #C6CFC6.
//
// Three things come out of that and none of them was true before. **The two accents differ** —
// Excluded is #C13A4A, not the same green; both buttons were sharing #4CBC18. **The icon takes
// the border's colour**, which is why both icons are `currentColor` now and the button owns the
// colour. And **the count never does**: it is #4F5F4A in all four drawn states, so it carries its
// own class rather than inheriting the accent, which is what it used to do.
//
// Hover and focus use the same accent as the selection. The frame is named for those states and
// draws one colour per button, so a hovered Included previewing the green it would become is the
// reading that fits both. All of it is classes rather than inline style: an inline borderColor
// beats every hover: variant, which is the trap already written up at fieldBorderCls in
// ComparableFilterRow.jsx.
// 36 tall, not 35: `Frame 458`'s rect is `x=1020.5 y=1012.5 width=64 height=35`, a half-pixel
// origin under whole-number dimensions with a plain (default 1px, centred) stroke — the stroke
// bleeds 0.5px past the geometric rect on every edge, so the visible box Figma reports is 36, which
// is what his letter states directly. Radius follows the height, half of it either way.
const chipCls = 'shrink-0 flex items-center justify-center gap-x-[6px] w-[64px] h-[36px] rounded-[18px] border transition-colors bg-[#F4FAEF] outline-none'

export default function IncludedExcludedToggle({ filters, onFiltersChange, onFiltersBlur, visibleCount, hiddenCount }) {
  const includedBtnSel = filters?.archivedView === 'active'
  const excludedBtnSel = filters?.archivedView === 'archived'

  return (
    <div className="flex gap-x-[5px] items-center">
      {/* show Qualified */}
      <button
        onClick={includedBtnSel ? undefined : () => {
          const next = { ...filters, archivedView: 'active' }
          onFiltersChange(next)
          onFiltersBlur?.(next)
        }}
        aria-pressed={includedBtnSel}
        className={`${chipCls} ${includedBtnSel ? INCLUDED_STATE.on : INCLUDED_STATE.off}`}
      >
        <IncludedIcon />
        <span className="text-[13px] font-semibold text-[#4F5F4A]">{visibleCount ?? 0}</span>
      </button>
      {/* show archived */}
      <button
        onClick={() => {
          const next = { ...filters, archivedView: 'archived' }
          onFiltersChange(next)
          onFiltersBlur?.(next)
        }}
        aria-pressed={excludedBtnSel}
        className={`${chipCls} ${excludedBtnSel ? EXCLUDED_STATE.on : EXCLUDED_STATE.off}`}
      >
        <ExcludedIcon />
        <span className="text-[13px] font-semibold text-[#4F5F4A]">{hiddenCount ?? 0}</span>
      </button>
    </div>
  )
}
